import { DialogComponent } from "./dialog.js";
import { fetchConfig } from "./utilities-v2.js";
import { morph } from "./morph.js";
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
} from "./events.js";
import {
  syncGiftQuantitiesSingleFlight,
  syncGiftQuantitiesQueued,
  getCart,
  primeCartCache,
} from "./pyp-gift-manager.js";
import { syncNuggetBundlesSingleFlight } from "./nugget-bundle.js";
import { initFreeGiftReconciler } from "./free-gift-manager.js";
import { cartStateManager } from "./cart-state-manager.js";
import { getEligibilityMessage } from "./promo-eligibility.js";
// Bare `@theme/*` specifier (resolved via the importmap in scripts.liquid), NOT
// a relative `./` path. The custom-box bundle (World B) imports the queue under
// this same specifier and marks it `external` in vite, so both worlds resolve to
// the one importmap URL and therefore share ONE queue instance. A relative import
// here would resolve to a different (unversioned) URL and silently fork into a
// second queue, breaking cross-surface serialization (ACQ-247 D1).
import { cartOperationQueue } from "@theme/cart-operation-queue";
// Pick-N cart-line shape (ACQ-247) — pure + stateless, relative import is fine.
import {
  buildGiftLineItems,
  groupGiftLinesToSelections,
} from "./gwp-cart-write.js";

/**
 * @typedef {Object} CartItemProperties
 * @property {string} [_is_pyp_gift] - Whether item is a PYP gift
 * @property {string} [_parent_item_id] - Parent item ID for gifts
 * @property {string} [_rb_id] - Recharge bundle ID
 */

/**
 * @typedef {Object} CartItem
 * @property {number} id - Line item ID
 * @property {string} key - Unique item key
 * @property {number} variant_id - Variant ID
 * @property {number} quantity - Item quantity
 * @property {string} product_type - Product type
 * @property {CartItemProperties} [properties] - Custom properties
 * @property {Object} [selling_plan_allocation] - Selling plan info
 * @property {string} handle - Item handle
 */

/**
 * @typedef {Object} Cart
 * @property {CartItem[]} items - Cart items
 * @property {number} item_count - Total item count
 * @property {number} total_price - Total price in cents
 * @property {number} total_discount - Total discount in cents
 * @property {Object[]} [cart_level_discount_applications] - Cart level discounts
 * @property {Object[]} [discount_codes] - Applied discount codes
 */

/**
 * @typedef {Object} GiftHandlerData
 * @property {string} [parentKey] - Parent item key
 * @property {string} [sellingPlanId] - Selling plan ID
 * @property {string} [quantity] - Item quantity
 * @property {string} [giftKey] - Existing gift key (for switching)
 * @property {string} [giftVariantId] - Existing gift variant ID (for switching)
 * @property {string} [parentUuid] - parent uuid identifier
 */

/**
 * Cart Drawer Component - uses section rendering pattern for updates
 * @extends {DialogComponent}
 */
class GRCartDrawerComponent extends DialogComponent {
  /** @type {boolean} */
  #loading = false;

  /** @type {Promise<void> | null} */
  #sectionRefreshPromise = null;

  /** @type {boolean} */
  #needsRefresh = true;

  /** @type {string | null} */
  #pendingSectionHtml = null;

  /** @type {number | undefined} */
  #bundleRenderTimeout;

  /** @type {number | undefined} */
  #safetyNetTimeout;

  /** @type {number | undefined} */
  #morphRafId;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {MutationObserver | null} */
  #optimizelyObserver = null;

  /** @param {Event} event */
  #handleQuantitySelectorUpdate = (event) => {
    // Event type is always QuantitySelectorUpdateEvent but comes through as Event
    this.#onQuantityChange(/** @type {any} */ (event));
  };

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;

    // Listen for cart updates from other components
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal,
    });

    // Listen for quantity selector updates (from cart-quantity-selector-component)
    document.addEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#handleQuantitySelectorUpdate,
      { signal },
    );

    // Custom events to open/close drawer
    document.addEventListener("gr-cart:open", this.#handleOpen, { signal });
    document.addEventListener("gr-cart:close", this.#handleClose, { signal });

    // Coupon input -> enable/disable Apply button
    this.addEventListener("input", this.#handleCouponInput, { signal });
    this.#updateCouponUiState();

    // Guard against Optimizely srcset hijacking — Optimizely's client-side JS
    // replaces srcset on <img> tags with its own CDN SVG tracker. Browsers
    // prefer srcset over src, so the real product image gets replaced with
    // an Optimizely placeholder. This observer strips it in real-time.
    this.#optimizelyObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.attributeName !== "srcset") continue;
        const img = /** @type {HTMLImageElement} */ (mutation.target);
        if (img.srcset?.includes("optimizely.com")) {
          img.removeAttribute("srcset");
        }
      }
    });
    this.#optimizelyObserver.observe(this, {
      subtree: true,
      attributes: true,
      attributeFilter: ["srcset"],
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    clearTimeout(this.#bundleRenderTimeout);
    this.#bundleRenderTimeout = undefined;
    clearTimeout(this.#safetyNetTimeout);
    this.#safetyNetTimeout = undefined;
    cancelAnimationFrame(this.#morphRafId);
    this.#morphRafId = undefined;
    this.#optimizelyObserver?.disconnect();
    this.#optimizelyObserver = null;
    // Clean up state manager
    cartStateManager.abortAllOperations();
    cartStateManager.clearAllLoading();
    // Do NOT clear cartOperationQueue here: it is now the shared importmap
    // singleton (ACQ-247 D1) and may hold operations owned by other surfaces
    // (gift drawer, custom-box commit). Clearing it on this drawer's teardown
    // would drop their pending work and break cross-surface serialization.
  }

  /** @param {Event} event */
  #handleCouponInput = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (target.getAttribute("ref") !== "couponInput") return;
    this.#updateCouponUiState();
  };

  #updateCouponUiState() {
    const { couponInput } = this.refs;
    if (!(couponInput instanceof HTMLInputElement)) return;

    const hasCode = couponInput.value.trim().length > 0;
    couponInput.disabled = this.#loading;
    this.#updateCouponButtonState(hasCode);
  }

  /** @param {Cart} cart */
  #cartOnlyHasGiftCards(cart) {
    if (!cart?.items?.length) return false;
    return cart.items.every((item) => {
      const productType =
        typeof item.product_type === "string"
          ? item.product_type.toLowerCase()
          : "";
      return productType === "gift card";
    });
  }

  #hideCouponWarning() {
    const { couponWarning } = this.refs;
    if (couponWarning instanceof HTMLElement) {
      couponWarning.classList.add("gr-hidden");
    }
  }

  #showCouponWarning() {
    const { couponWarning } = this.refs;
    if (couponWarning instanceof HTMLElement) {
      couponWarning.classList.remove("gr-hidden");
    }
  }

  /**
   * Shows/hides the subscription-only promo eligibility message. Called from
   * #finalizeCartUpdate, the single shared tail every cart mutation
   * (add/remove/qty/gift-claim/subscription-upgrade) funnels through, so it
   * re-evaluates on every cart change without wiring a check into each flow.
   * @param {Cart} cart
   */
  #updatePromoEligibilityMessage(cart) {
    const { promoEligibilityMessage } = this.refs;
    if (!(promoEligibilityMessage instanceof HTMLElement)) return;

    const message = getEligibilityMessage(cart, window.promoCampaignMap || {});
    if (message) {
      promoEligibilityMessage.textContent = message;
      promoEligibilityMessage.classList.remove("gr-hidden");
    } else {
      promoEligibilityMessage.classList.add("gr-hidden");
    }
  }

  /**
   * Opens the cart drawer
   */
  async open() {
    // Clear stale edit state when opening drawer outside the builder
    // Prevents spurious beforeunload dialogs (e.g. adding GWP from cart)
    if (
      typeof Alpine !== "undefined" &&
      !window.location.pathname.startsWith("/pages/custom-box")
    ) {
      const store = Alpine.store("customBox");
      if (store && store.editingCartItemKey) {
        store.editingCartItemKey = null;
        store.originalCuts = null;
        store.originalSellingPlanId = null;
        store.originalDeliveryFrequency = null;
        store.originalGwpVariantId = null;
        store.saveToStorage();
      }
    }

    // Refresh content if it was updated while closed

    const didMorph = this.#needsRefresh;
    if (this.#needsRefresh) {
      this.#pendingSectionHtml = null;
      await this.#fetchAndMorphSection();
      this.#needsRefresh = false;
    }
    this.showDialog();

    if (!didMorph) {
      // Render bundle cuts on initial open (morph path handles its own render)
      this.querySelectorAll(".gr-bundle-cuts[data-bundle-contents]").forEach(
        (container) => {
          this.#renderBundleCuts(container);
        },
      );
    }

    // Safety net: re-render any empty bundle cuts containers after dialog shows.
    // Catches edge cases where open() render was skipped (didMorph path)
    // and the debounced post-morph render hasn't fired yet.
    clearTimeout(this.#safetyNetTimeout);
    this.#safetyNetTimeout = setTimeout(() => {
      this.querySelectorAll(".gr-bundle-cuts[data-bundle-contents]").forEach(
        (container) => {
          if (!container.children.length) this.#renderBundleCuts(container);
        },
      );
    }, 100);

    // ACQ-210 #1 STATE: stale-tolerant analytics read. The coherent cart-state is
    // fine for a `custom_cart_viewed` payload, and the drawer has just morphed so
    // the cache is warm; no need to force a network read here.
    const cart = await getCart();

    // The refresh-on-open morph above (didMorph path) replaces the footer
    // with its server-rendered (hidden) state, same as any other morph.
    this.#updatePromoEligibilityMessage(cart);

    if (Shopify?.analytics?.publish) {
      Shopify.analytics.publish("custom_cart_viewed", { data: cart });
    }
  }

  /**
   * Closes the cart drawer
   */
  close() {
    this.closeDialog();
  }

  #handleOpen = () => {
    this.open();
  };

  #handleClose = () => {
    this.close();
  };

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;
    if (!sectionId) throw new Error("Section id missing");
    return sectionId;
  }

  /**
   * Extracts a cart payload from a cart event when available.
   * @param {CartUpdateEvent} event
   * @returns {Cart | null}
   */
  #extractCartFromEvent(event) {
    const resourceCart = event.detail?.resource;
    const resourceWithItems = resourceCart
      ? /** @type {any} */ (resourceCart)
      : null;
    if (resourceWithItems && Array.isArray(resourceWithItems.items)) {
      return /** @type {Cart} */ (resourceWithItems);
    }

    const dataCart = event.detail?.data?.cart;
    const dataWithItems = dataCart ? /** @type {any} */ (dataCart) : null;
    if (dataWithItems && Array.isArray(dataWithItems.items)) {
      return /** @type {Cart} */ (dataWithItems);
    }

    return null;
  }

  /**
   * Primes the shared cart cache when a cart snapshot is available.
   *
   * ACQ-210: queued mutations pass their write-queue sequence
   * (`cartOperationQueue.currentSequence`) so `primeCartCache` can reject a prime that
   * resolves out of order behind a newer write (the monotonic guard). Callers outside the
   * queue (the cross-component `cart:update` handler) omit it and are always applied.
   * @param {Cart | null} cart
   * @param {number} [sequence] - Write-queue sequence; omit for non-queued callers.
   */
  #primeCart(cart, sequence) {
    if (cart && Array.isArray(cart.items)) {
      primeCartCache(cart, sequence);
    }
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event
   * @param {QuantitySelectorUpdateEvent} event
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;
    if (!line) return;

    // Abort any pending operation for this line
    cartStateManager.abortLineOperation(line);

    // Find the item key from the DOM
    const itemKey = this.#findItemKeyByLine(line);

    // Lock this line immediately (prevents spam clicking + hides number before request)
    cartStateManager.setLineLoading(line);
    cartStateManager.setKeyLoading(itemKey);
    this.#setLineQuantityLoading(line, true);

    if (quantity === 0) {
      return this.onLineItemRemove(line, itemKey);
    }

    this.#updateQuantity(line, quantity, itemKey);
  }

  /**
   * Finds the item key from the DOM by line number
   * @param {number} line - The 1-based line number
   * @returns {string | null} The item key or null if not found
   */
  #findItemKeyByLine(line) {
    const cartItem = this.querySelector(`.gr-cart-item[data-line="${line}"]`);
    return cartItem?.getAttribute("data-key") || null;
  }

  /**
   * Handles line item removal
   * @param {number} line - The line item index
   * @param {string | null} [itemKey] - The item key
   */
  onLineItemRemove(line, itemKey) {
    this.#updateQuantity(line, 0, itemKey);
  }

  #renderBundleCuts(container) {
    const raw = container.dataset.bundleContents;
    if (!raw) return;
    let contents;
    try {
      contents = JSON.parse(raw);
    } catch (e) {
      console.error("parse error", e);
      return;
    }
    if (contents.v !== 1 || !Array.isArray(contents.cuts)) return;
    const ul = document.createElement("ul");
    ul.className = "list-none p-0 m-0";
    for (const cut of contents.cuts) {
      if (!cut) continue;
      const li = document.createElement("li");
      li.className = "text-[14px] text-grayscale-coal-500";
      const qty = cut.q ?? "";
      const title = cut.t ?? "";
      li.textContent = `${qty} × ${title}`;
      ul.appendChild(li);
    }
    container.replaceChildren(ul);
  }

  /**
   * Handles cart update events from other components
   * @param {CartUpdateEvent} event
   */
  #handleCartUpdate = async (event) => {
    // Skip if update came from this component
    if (event.detail.sourceId === "gr-cart-drawer") return;

    const incomingCart = this.#extractCartFromEvent(event);
    this.#primeCart(incomingCart);

    const isDialogOpen = this.refs.dialog?.open;
    const sectionsHtml = event.detail.data?.sections?.[this.sectionId];

    // Only morph when dialog is open to prevent unexpected reopening
    if (isDialogOpen) {
      if (sectionsHtml) {
        this.#morphCartContainers(sectionsHtml);
        this.#updateCartQuantitySelectorButtonStates();
      } else {
        // If no section HTML provided, fetch fresh
        await this.#fetchAndMorphSection();
      }
      // Both morph paths above replace the footer with its server-rendered
      // (hidden) state, so the eligibility message has to be re-derived after
      // them. This listener handles cart updates from other components
      // (custom-box builder, gift manager, etc.) that #finalizeCartUpdate's
      // own re-derivation never sees.
      this.#updatePromoEligibilityMessage(incomingCart);
    } else {
      // Dialog is closed - mark that we need to refresh content when drawer opens next
      // Don't update cart bubble here - let cart-icon component handle it
      this.#needsRefresh = true;
      // Cache the section HTML from the Cart API response so we can use it
      // when the drawer opens, instead of relying on the Section Rendering API
      // which fails for section group sections on certain page URLs
      this.#pendingSectionHtml = sectionsHtml || null;
    }

    // Sync PYP gift quantities when cart updates from cart-items-component.
    // This is a REACTIVE cart:update listener (can fire mid-commit), so route
    // the reconcile through the queue with a fresh read inside (ACQ-247 D2)
    // rather than mutating off-queue against this transient event snapshot.
    if (event.detail.data?.source === "cart-items-component") {
      try {
        await syncGiftQuantitiesQueued({
          dispatchEvent: false,
          sections: [this.sectionId],
          // Cart page removals carry their pre-change cart so add-on cleanup
          // can tell a removed box from an add-on-only cart (ACQ-433).
          previousCart: event.detail.data?.previousCart ?? null,
        });
      } catch (error) {
        console.error("Error syncing gift quantities:", error);
      }
    }
  };

  /**
   * Updates the cart count bubble in the trigger button
   * @param {number} count - The cart item count
   */
  #updateCartBubble(count) {
    // Update both the trigger bubble and any header bubbles
    const bubbles = this.querySelectorAll(".cart-bubble");
    const bubbleCounts = this.querySelectorAll(".cart-bubble__text-count");

    bubbles.forEach((bubble) => {
      bubble.classList.toggle("!hidden", count === 0);
    });

    bubbleCounts.forEach((bubbleCount) => {
      bubbleCount.textContent = count < 100 ? String(count) : "99+";
      bubbleCount.classList.toggle("!hidden", count === 0);
    });

    // Keep the screen reader count in step with the visible one (ACQ-376).
    // The cart:update dispatched right after also refreshes it via cart-icon,
    // but this writer must not leave the two out of sync on its own.
    this.querySelectorAll('[ref="cartBubbleA11yCount"]').forEach((a11yCount) => {
      a11yCount.textContent = String(count);
    });
  }

  /**
   * Morphs only the cart items and footer containers (targeted updates)
   * This prevents dialog state issues by leaving the dialog shell intact
   * @param {string} sectionHtml - The full section HTML
   */
  #morphCartContainers(sectionHtml) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(sectionHtml, "text/html");

    // Morph cart items container
    const newItemsContainer = doc.querySelector(
      `#cart-items-${this.sectionId}`,
    );
    const oldItemsContainer = this.querySelector(
      `#cart-items-${this.sectionId}`,
    );

    if (newItemsContainer && oldItemsContainer) {
      morph(oldItemsContainer, newItemsContainer);
    }

    // Morph footer container
    const newFooterContainer = doc.querySelector(
      `#cart-footer-${this.sectionId}`,
    );
    const oldFooterContainer = this.querySelector(
      `#cart-footer-${this.sectionId}`,
    );

    if (newFooterContainer && oldFooterContainer) {
      morph(oldFooterContainer, newFooterContainer);
    }

    // Update empty/content visibility from live DOM after morph
    const liveItems = this.querySelectorAll(".gr-cart-item");
    const itemCount = liveItems.length;

    const { emptyState, cartContent } = this.refs;

    if (emptyState instanceof HTMLElement) {
      emptyState.classList.toggle("gr-hidden", itemCount > 0);
    }
    if (cartContent instanceof HTMLElement) {
      cartContent.classList.toggle("gr-hidden", itemCount === 0);
    }

    // Defer quantity selector updates to next frame to ensure DOM is settled
    cancelAnimationFrame(this.#morphRafId);
    this.#morphRafId = requestAnimationFrame(() => {
      // Restore loading states from centralized manager
      this.#restoreLoadingStates();
      this.#updateCartQuantitySelectorButtonStates();
    });

    // Debounce bundle cuts render to coalesce rapid morph cycles
    clearTimeout(this.#bundleRenderTimeout);
    this.#bundleRenderTimeout = setTimeout(() => {
      this.querySelectorAll(".gr-bundle-cuts[data-bundle-contents]").forEach(
        (container) => this.#renderBundleCuts(container),
      );
    }, 50);
  }

  /**
   * Restores loading states after a morph using item keys (stable identifiers).
   * Line numbers shift when items are removed, so key-based lookup prevents
   * applying loading to the wrong item after a cart mutation.
   */
  #restoreLoadingStates() {
    // First clear ALL stale loading classes that survived the morph
    // (morph can't remove them due to data-skip-node-update on qty selectors)
    this.#forceRestoreAllQuantitySelectors();

    // Re-apply loading only for items whose KEY is still being processed
    const loadingKeys = cartStateManager.getLoadingKeys();
    for (const key of loadingKeys) {
      try {
        this.#setItemLoadingByKey(key, true);
      } catch (error) {
        console.error(`Failed to restore loading state for key ${key}:`, error);
      }
    }

    // Regression guard: detect if any qty selector has loading class but its
    // item key is NOT in the active loading set. This catches line-number
    // desync regressions where loading leaks to unrelated items.
    const allQtySelectors = this.querySelectorAll(
      "cart-quantity-selector-component.gr-cart-qty",
    );
    for (const qs of allQtySelectors) {
      if (!qs.classList.contains("gr-cart-qty--loading")) continue;
      const itemKey = qs.closest(".gr-cart-item")?.getAttribute("data-key");
      if (itemKey && !loadingKeys.includes(itemKey)) {
        console.warn(
          `[cart-drawer] Loading state leak detected: key ${itemKey} has loading class but is not in active loading set. Clearing.`,
        );
        this.#setQuantitySelectorState(/** @type {HTMLElement} */ (qs), false);
      }
    }
  }

  /**
   * Fetches and morphs the section using the Cart API.
   * Uses /cart/update.js with a sections parameter instead of the Section
   * Rendering API (?section_id= on the current URL) because section group
   * sections (like the header) are not resolvable from all page URLs.
   */
  async #fetchAndMorphSection() {
    if (this.#sectionRefreshPromise) return this.#sectionRefreshPromise;

    this.#sectionRefreshPromise = (async () => {
      try {
        const response = await fetch(
          `${Theme.routes.cart_update_url}`,
          fetchConfig("json", {
            body: JSON.stringify({ sections: this.sectionId }),
          }),
        );
        const data = await response.json();
        const sectionHtml = data.sections?.[this.sectionId];

        if (sectionHtml) {
          this.#morphCartContainers(sectionHtml);
          this.#updateCartQuantitySelectorButtonStates();
          this.#updateCouponUiState();
        }
      } catch (error) {
        console.error("Error fetching section:", error);
      } finally {
        this.#sectionRefreshPromise = null;
      }
    })();

    return this.#sectionRefreshPromise;
  }

  /**
   * Fetches cart data from Shopify
   * @returns {Promise<Cart>}
   */
  async #fetchCart(force = false) {
    return /** @type {Promise<Cart>} */ (getCart({ force }));
  }

  /**
   * Finalizes cart update: morph UI, update bubble, dispatch event
   * @param {Cart} cart - The updated cart
   * @param {string} [sectionHtml] - Optional section HTML to morph
   * @param {Record<string, string>} [sections] - Optional sections data
   * @param {number} [itemCount] - Optional item count override
   */
  async #finalizeCartUpdate(cart, sectionHtml, sections, itemCount) {
    // Always reached from inside a queued operation, so currentSequence is this write's
    // sequence — prime in write-queue completion order (ACQ-210 monotonic guard).
    this.#primeCart(cart, cartOperationQueue.currentSequence);
    const count = itemCount ?? cart.item_count;

    if (sectionHtml) {
      this.#morphCartContainers(sectionHtml);
    } else {
      await this.#fetchAndMorphSection();
    }

    // promoEligibilityMessage lives inside the footer container the morph
    // above just replaced with its server-rendered (hidden) state, so this
    // has to run after the morph, not before it.
    this.#updatePromoEligibilityMessage(cart);
    this.#updateCartBubble(count);

    document.dispatchEvent(
      new CartUpdateEvent(cart, "gr-cart-drawer", {
        source: "gr-cart-drawer",
        itemCount: count,
        sections,
        cart,
      }),
    );
  }

  /**
   * Syncs gifts and bundles after cart operations
   * @param {Cart} cart - Initial cart result
   * @param {Record<string, string>} [initialSections] - Initial sections from cart operation
   * @param {Cart | null} [previousCart] - Cart before the operation; lets add-on
   *   cleanup tell a removed box from a cart that was add-on-only all along (ACQ-433)
   * @returns {Promise<{cart: Cart, sections: Record<string, string>|undefined, sectionHtml: string|undefined}>}
   */
  async #syncGiftsAndBundles(cart, initialSections, previousCart = null) {
    let currentCart = cart;
    let mergedSections = initialSections ? { ...initialSections } : undefined;
    let sectionHtml = mergedSections?.[this.sectionId];

    // Sync PYP gift quantities
    const giftSyncResult = await syncGiftQuantitiesSingleFlight(currentCart, {
      dispatchEvent: false,
      sections: [this.sectionId],
      previousCart,
    });

    currentCart = giftSyncResult.cart || currentCart;
    if (giftSyncResult.sections) {
      mergedSections = {
        ...(mergedSections || {}),
        ...giftSyncResult.sections,
      };
      sectionHtml = giftSyncResult.sections[this.sectionId] || sectionHtml;
    }

    // Sync nugget bundles
    const nuggetSyncResult = await syncNuggetBundlesSingleFlight(currentCart, {
      dispatchEvent: false,
      sections: [this.sectionId],
    });

    if (nuggetSyncResult.cart) {
      currentCart = /** @type {Cart} */ (nuggetSyncResult.cart);
    }
    if (nuggetSyncResult.sections) {
      mergedSections = {
        ...(mergedSections || {}),
        ...nuggetSyncResult.sections,
      };
      sectionHtml = nuggetSyncResult.sections[this.sectionId] || sectionHtml;
    }

    // Handle case where nugget sync made changes but didn't return sections
    if (nuggetSyncResult.updated && !nuggetSyncResult.sections) {
      sectionHtml = undefined;
    }

    return { cart: currentCart, sections: mergedSections, sectionHtml };
  }

  /**
   * Updates coupon UI state (button styling and enabled/disabled)
   * @param {boolean} [hasCode] - Whether coupon input has a value
   */
  #updateCouponButtonState(hasCode) {
    const { couponApplyBtn } = this.refs;
    if (!(couponApplyBtn instanceof HTMLButtonElement)) return;

    const isEnabled = hasCode && !this.#loading;
    couponApplyBtn.disabled = this.#loading;

    couponApplyBtn.classList.toggle("bg-black-warm", isEnabled);
    couponApplyBtn.classList.toggle("text-white", isEnabled);
    couponApplyBtn.classList.toggle("hover:bg-black-warm-hover", isEnabled);
    couponApplyBtn.classList.toggle("bg-grayscale-coal-100", !isEnabled);
    couponApplyBtn.classList.toggle("text-grayscale-coal-400", !isEnabled);
  }

  /**
   * Updates cart item quantity using section rendering
   * Uses item key (id) when available, falls back to line number
   * Wrapped in operation queue to prevent race conditions
   * @param {number} line - The 1-based line number
   * @param {number} quantity - The new quantity
   * @param {string | null} [itemKey] - The item key
   */
  async #updateQuantity(line, quantity, itemKey) {
    // Queue this operation to serialize cart updates
    return cartOperationQueue.enqueue(
      () => this.#executeUpdateQuantity(line, quantity, itemKey),
      { id: `update-line-${line}`, priority: 1 },
    );
  }

  /**
   * Executes the actual quantity update operation
   * @param {number} line
   * @param {number} quantity
   * @param {string | null} [itemKey]
   */
  async #executeUpdateQuantity(line, quantity, itemKey) {
    const loadingLine = line; // capture for finally block (immune to overwrites)
    const loadingKey = itemKey; // capture item key for finally block
    this.#loading = true;
    cartStateManager.setLineLoading(line);
    cartStateManager.setKeyLoading(itemKey);
    // Apply loading visually by key (stable across morphs) with line fallback
    if (itemKey) {
      this.#setItemLoadingByKey(itemKey, true);
    } else {
      this.#setLineQuantityLoading(line, true);
    }
    this.#disableCart();

    // Create abort controller for this operation
    const abortController =
      cartStateManager.createLineOperationController(line);

    try {
      // Add-on cleanup after this change is orphan cleanup: it must see the
      // cart as it was BEFORE the change to know a box has just left (ACQ-433).
      // Only a removal can orphan anything, so only a removal pays for the read.
      /** @type {Cart | null} */
      let previousCart = null;
      if (quantity === 0) {
        try {
          previousCart = await this.#fetchCart();
        } catch (error) {
          console.error("Error reading cart before removal:", error);
        }
      }

      // Use item key (id) when available, falls back to line number
      const updatePayload = itemKey
        ? { id: itemKey, quantity }
        : { line, quantity };

      const body = JSON.stringify({
        ...updatePayload,
        sections: this.sectionId,
        sections_url: window.location.pathname,
      });

      const response = await fetch(Theme.routes.cart_change_url, {
        ...fetchConfig("json", { body }),
        signal: abortController.signal,
      });
      const result = await response.json();

      if (result.errors) {
        console.error("Cart update error:", result.errors);
        return;
      }

      this.#primeCart(result, cartOperationQueue.currentSequence);

      // Sync gifts and bundles
      const {
        cart: finalCart,
        sections: mergedSections,
        sectionHtml,
      } = await this.#syncGiftsAndBundles(
        result,
        result.sections,
        previousCart,
      );

      await this.#finalizeCartUpdate(finalCart, sectionHtml, mergedSections);
    } catch (error) {
      // Ignore AbortError (operation was cancelled intentionally)
      if (error.name === "AbortError") {
        console.log("Cart operation cancelled, newer request in flight");
        return;
      }
      console.error("Error updating quantity:", error);
      // Force re-enable all quantity selectors on error
      this.#forceRestoreAllQuantitySelectors();
    } finally {
      this.#loading = false;
      cartStateManager.clearLineLoading(loadingLine);
      cartStateManager.clearKeyLoading(loadingKey);
      cartStateManager.clearLineOperationController(loadingLine);
      // Use try-catch to ensure state is always cleared even if DOM queries fail
      try {
        this.#setLineQuantityLoading(loadingLine, false);
        if (loadingKey) this.#setItemLoadingByKey(loadingKey, false);
      } catch (loadingError) {
        console.error("Error restoring loading state:", loadingError);
      }
      // Safety: if queue is empty, clear any stale loading states
      if (!cartOperationQueue.isProcessing && cartOperationQueue.length === 0) {
        cartStateManager.clearAllLoading();
        this.#forceRestoreAllQuantitySelectors();
      }
      this.#enableCart();
    }
  }

  /**
   * Sets the loading/disabled state of a quantity selector
   * @param {HTMLElement} qtySelector - The quantity selector element
   * @param {boolean} isLoading - Whether to show loading state
   * @param {boolean} useComponentAPI - Whether to try component API first
   */
  #setQuantitySelectorState(qtySelector, isLoading, useComponentAPI = true) {
    // Try component API first (preferred method)
    if (
      useComponentAPI &&
      "setLoading" in qtySelector &&
      typeof (/** @type {any} */ (qtySelector).setLoading) === "function"
    ) {
      /** @type {any} */ (qtySelector).setLoading(isLoading);
      return;
    }

    // Fallback: direct DOM manipulation
    const loader = qtySelector.querySelector("[data-qty-loader]");
    if (loader instanceof HTMLElement) {
      loader.classList.toggle("hidden", !isLoading);
    }

    const minus = qtySelector.querySelector('button[ref="minusButton"]');
    const plus = qtySelector.querySelector('button[ref="plusButton"]');
    if (minus instanceof HTMLButtonElement) minus.disabled = isLoading;
    if (plus instanceof HTMLButtonElement) plus.disabled = isLoading;

    qtySelector.classList.toggle("gr-cart-qty--loading", isLoading);
    qtySelector.setAttribute("aria-busy", isLoading ? "true" : "false");
  }

  /**
   * Shows/hides the quantity loader for a specific cart line.
   * Only replaces the number slot; +/- button styling remains unchanged.
   * @param {number} line
   * @param {boolean} isLoading
   */
  #setLineQuantityLoading(line, isLoading) {
    if (!line) {
      console.warn("setLineQuantityLoading called with invalid line:", line);
      return;
    }

    // Sync with centralized state
    if (isLoading) {
      cartStateManager.setLineLoading(line);
    } else {
      cartStateManager.clearLineLoading(line);
    }

    const cartItem = this.querySelector(`.gr-cart-item[data-line="${line}"]`);
    if (!(cartItem instanceof HTMLElement)) {
      console.warn(`Cart item not found for line ${line}`);
      return;
    }

    const qtySelector = cartItem.querySelector(
      "cart-quantity-selector-component.gr-cart-qty",
    );
    if (!(qtySelector instanceof HTMLElement)) {
      console.warn(`Quantity selector not found for line ${line}`);
      return;
    }

    try {
      this.#setQuantitySelectorState(qtySelector, isLoading);
    } catch (error) {
      console.error(`Error setting loading state for line ${line}:`, error);
      if (!isLoading) {
        this.#forceRestoreAllQuantitySelectors();
      }
    }
  }

  /**
   * Sets loading state on a quantity selector found by item key.
   * Item keys are stable across cart mutations (unlike line numbers which shift).
   * @param {string} key - The cart item key (from data-key attribute)
   * @param {boolean} isLoading
   */
  #setItemLoadingByKey(key, isLoading) {
    if (!key) return;

    const cartItem = this.querySelector(`.gr-cart-item[data-key="${key}"]`);
    if (!cartItem) return; // Item was removed from cart — nothing to update

    const qtySelector = cartItem.querySelector(
      "cart-quantity-selector-component.gr-cart-qty",
    );
    if (!(qtySelector instanceof HTMLElement)) return;

    try {
      this.#setQuantitySelectorState(qtySelector, isLoading);
    } catch (error) {
      console.error(`Error setting loading state for key ${key}:`, error);
    }
  }

  /**
   * Disables cart interactions during updates
   */
  #disableCart() {
    this.classList.add("gr-cart-disabled");
  }

  /**
   * Enables cart interactions after updates
   */
  #enableCart() {
    this.classList.remove("gr-cart-disabled");
  }

  /**
   * Updates button states for all cart quantity selector components
   */
  #updateCartQuantitySelectorButtonStates() {
    const cartQuantitySelectors = this.querySelectorAll(
      "cart-quantity-selector-component",
    );
    for (const selector of cartQuantitySelectors) {
      if (
        "updateButtonStates" in selector &&
        typeof selector.updateButtonStates === "function"
      ) {
        selector.updateButtonStates();
      }
    }
  }

  /**
   * Emergency recovery: force restore all quantity selectors to enabled state
   * Used when loading state gets stuck due to errors or race conditions
   */
  #forceRestoreAllQuantitySelectors() {
    console.warn("Force restoring all quantity selectors");
    const cartQuantitySelectors = this.querySelectorAll(
      "cart-quantity-selector-component.gr-cart-qty",
    );

    for (const qtySelector of cartQuantitySelectors) {
      if (!(qtySelector instanceof HTMLElement)) continue;
      try {
        this.#setQuantitySelectorState(qtySelector, false);
      } catch (error) {
        console.error("Error in force restore for selector:", error);
      }
    }

    // Also update button states based on inventory limits
    this.#updateCartQuantitySelectorButtonStates();
  }

  /**
   * Handles coupon apply
   */
  async handleApplyCoupon() {
    const { couponInput, couponWarning } = this.refs;
    if (!couponInput || this.#loading) return;

    const code = /** @type {HTMLInputElement} */ (couponInput).value.trim();
    if (!code) return;

    // Queue this operation
    return cartOperationQueue.enqueue(() => this.#executeApplyCoupon(code), {
      id: `apply-coupon-${code}`,
      priority: 2,
    });
  }

  /**
   * Executes the coupon apply operation
   * @param {string} code
   */
  async #executeApplyCoupon(code) {
    const { couponInput, couponWarning } = this.refs;
    if (!couponInput) return;

    this.#loading = true;
    this.#hideCouponWarning();
    this.#updateCouponUiState();
    this.#disableCart();

    try {
      const cartBefore = await this.#fetchCart(true);
      this.#primeCart(cartBefore, cartOperationQueue.currentSequence);

      if (this.#cartOnlyHasGiftCards(cartBefore)) {
        this.#showCouponWarning();
        return;
      }

      await fetch("/discount/" + encodeURIComponent(code));

      const cartAfter = await this.#fetchCart(true);

      // Check if the discount code was applicable
      const appliedCode = cartAfter.discount_codes?.find(
        (dc) =>
          /** @type {any} */ (dc).code?.toLowerCase?.() === code.toLowerCase(),
      );
      if (/** @type {any} */ (appliedCode)?.applicable === false) {
        alert("Sorry, couldn't apply discount.");
        return;
      }

      // Clear input + warning
      /** @type {HTMLInputElement} */ (couponInput).value = "";
      this.#hideCouponWarning();

      await this.#finalizeCartUpdate(cartAfter);
    } catch (error) {
      console.error("Error applying coupon:", error);
    } finally {
      this.#loading = false;
      this.#enableCart();
      this.#updateCouponUiState();
      if (couponWarning instanceof HTMLElement) {
        // Ensure warning isn't sticky like TSX
        this.#hideCouponWarning();
      }
    }
  }

  /**
   * Handles coupon removal
   */
  async handleRemoveCoupon() {
    if (this.#loading) return;

    // Queue this operation
    return cartOperationQueue.enqueue(() => this.#executeRemoveCoupon(), {
      id: "remove-coupon",
      priority: 2,
    });
  }

  /**
   * Executes the coupon removal operation
   */
  async #executeRemoveCoupon() {
    const { couponRemoveBtn } = this.refs;
    if (this.#loading) return;

    this.#loading = true;
    this.#updateCouponUiState();
    this.#disableCart();

    // Disable remove button while processing
    if (couponRemoveBtn instanceof HTMLButtonElement) {
      couponRemoveBtn.disabled = true;
    }

    try {
      // Shopify clears discount codes when you POST an empty discount string
      const body = JSON.stringify({
        discount: "",
        sections: this.sectionId,
        sections_url: window.location.pathname,
      });
      await fetch(Theme.routes.cart_update_url, fetchConfig("json", { body }));

      const cart = await this.#fetchCart(true);
      await this.#finalizeCartUpdate(cart);
    } catch (error) {
      console.error("Error removing coupon:", error);
    } finally {
      this.#loading = false;
      this.#enableCart();
      this.#updateCouponUiState();
    }
  }

  /**
   * Checks if the cart only contains items that can skip the add-ons page
   * (gift boxes, gift cards, bundles, or PYP gifts).
   * @param {{ items: Array<any> }} cart
   * @returns {boolean}
   */
  #canSkipAddons(cart) {
    if (!cart.items || cart.items.length === 0) return true;

    const BEEF_STICK_SKUS = new Set(["BOX-BFSTX", "BNDL-BFSTX"]);
    return cart.items.every((item) => {
      const productType =
        typeof item.product_type === "string"
          ? item.product_type.toLowerCase()
          : "";
      const isGiftBox = productType === "gift box";
      const isGiftCard = item.handle === "digital-gift-card";
      const isBundle = !!(item.properties && item.properties._rb_id);
      const isPypGift = !!(
        item.properties && item.properties._is_pyp_gift === "true"
      );
      // Legacy nugget bundle-flow lines (retired builder, ACQ-378): carts
      // created before this release can still carry these markers, so they
      // keep their old checkout routing. New adds never set these properties.
      const isLegacyNuggetLine = !!(
        item.properties &&
        (item.properties._is_nugget_onetime === "true" ||
          item.properties._is_nugget_bundle === "true")
      );
      // Promo free-gift lines (ACQ-299) ride along with whatever the cart
      // already is; they never force the add-ons interstitial.
      const isFreeGift = !!(item.properties && item.properties._free_gift);
      const isBeefStick =
        typeof item.sku === "string" &&
        BEEF_STICK_SKUS.has(item.sku.trim().toUpperCase());
      return (
        isGiftBox ||
        isGiftCard ||
        isBundle ||
        isPypGift ||
        isLegacyNuggetLine ||
        isFreeGift ||
        isBeefStick
      );
    });
  }

  /**
   * Handles checkout button click
   */
  async handleCheckout() {
    const cart = await this.#fetchCart();

    if (!cart.items || cart.items.length === 0) {
      this.close();
      return;
    }

    const canSkipAddons = this.#canSkipAddons(cart);

    // Check if on addons page
    const isAddonsPage = window.location.pathname.includes(
      "/pre-checkout-add-ons",
    );

    if (canSkipAddons || isAddonsPage) {
      window.location.href = "/checkout";
    } else {
      window.location.href = "/collections/pre-checkout-add-ons";
    }
  }

  /**
   * Handles coupon input keydown
   * @param {KeyboardEvent} event
   */
  handleCouponKeydown(event) {
    if (event.key === "Enter") {
      event.preventDefault();
      this.handleApplyCoupon();
    }
  }

  /**
   * Handles upgrading a one-time purchase to a subscription
   * @param {Object} data - Data from on:click handler
   * @param {string} data.lineKey - Cart line item key
   * @param {string} data.variantId - Variant ID
   * @param {string} data.sellingPlanId - Selling plan ID to use
   * @param {string} data.quantity - Current item quantity
   * @param {MouseEvent} _event
   */
  async handleUpgradeToSubscription(data, _event) {
    const lineKey = data?.lineKey;
    const sellingPlanId = data?.sellingPlanId;
    const quantity = parseInt(data?.quantity || "1", 10);
    const productId = data?.productId;
    const productName = data?.productName;
    const frequency = data?.frequency;

    if (!lineKey || !sellingPlanId) {
      console.error("Cannot upgrade to subscription: missing data", {
        lineKey,
        sellingPlanId,
      });
      return;
    }

    if (this.#loading) return;

    // Queue this operation
    return cartOperationQueue.enqueue(
      () =>
        this.#executeUpgradeToSubscription(lineKey, sellingPlanId, quantity, {
          frequency,
          productId,
          productName,
        }),
      { id: `upgrade-${lineKey}`, priority: 1 },
    );
  }

  /**
   * Executes the subscription upgrade operation
   * @param {string} lineKey
   * @param {string} sellingPlanId
   * @param {number} quantity
   * @param {{frequency?: string, productId?: string, productName?: string}} analytics
   */
  async #executeUpgradeToSubscription(
    lineKey,
    sellingPlanId,
    quantity,
    { frequency, productId, productName } = {},
  ) {
    this.#loading = true;
    this.#disableCart();

    try {
      const body = JSON.stringify({
        id: lineKey,
        quantity,
        selling_plan: sellingPlanId,
        sections: this.sectionId,
        sections_url: window.location.pathname,
      });

      const response = await fetch(
        Theme.routes.cart_change_url,
        fetchConfig("json", { body }),
      );
      const result = await response.json();

      if (result.errors) {
        console.error("Upgrade to subscription error:", result.errors);
        return;
      }

      // Track analytics
      const cartContext =
        window.grAnalytics?.buildCartContextFromCart?.(result) || undefined;
      window.grAnalytics?.trackSubscriptionBoxAdded?.(
        {
          frequency: frequency || undefined,
          product_id: productId,
          product_name: productName,
        },
        cartContext,
      );

      await this.#finalizeCartUpdate(
        result,
        result.sections?.[this.sectionId],
        result.sections,
      );
    } catch (error) {
      console.error("Error upgrading to subscription:", error);
      // Force re-enable all quantity selectors on error
      this.#forceRestoreAllQuantitySelectors();
    } finally {
      this.#loading = false;
      this.#enableCart();
    }
  }

  /**
   * Opens the gift selection drawer for claiming a new gift
   * @param {GiftHandlerData} data - Data from on:click handler
   * @param {MouseEvent} _event
   */
  async handleClaimGift(data, _event) {
    const parentKey = data?.parentKey;
    const parentUuid = data?.parentUuid;
    const parentSellingPlanId = data?.sellingPlanId;
    const parentQuantity = data?.quantity || 1;

    if (!parentKey) {
      console.error("Cannot claim gift: missing parent key");
      return;
    }

    const cart = await this.#fetchCart();
    const canSkipAddons = this.#canSkipAddons(cart);

    const giftDrawer = /** @type {any} */ (
      document.querySelector("gift-selection-drawer")
    );

    if (giftDrawer && typeof giftDrawer.showSelectionMode === "function") {
      giftDrawer.showSelectionMode({
        parentItemKey: parentKey,
        parentUuid: parentUuid,
        parentSellingPlanId,
        parentQuantity:
          typeof parentQuantity === "number"
            ? parentQuantity
            : parseInt(parentQuantity, 10),
        skipAddonsRedirect: canSkipAddons,
      });
    }
  }

  /**
   * Opens the gift selection drawer for switching an existing gift
   * @param {GiftHandlerData} data - Data from on:click handler
   * @param {MouseEvent} _event
   */
  handleSwitchGift(data, _event) {
    const parentKey = data?.parentKey;
    const parentUuid = data?.parentUuid; // ← ADD THIS
    const existingGiftKey = data?.giftKey;
    const existingGiftVariantId = data?.giftVariantId;
    const parentQuantity = data?.quantity || 1;

    if (!parentKey || !existingGiftKey) {
      console.error("Cannot switch gift: missing keys", {
        parentKey,
        existingGiftKey,
      });
      return;
    }

    const giftDrawer = /** @type {any} */ (
      document.querySelector("gift-selection-drawer")
    );
    if (giftDrawer && typeof giftDrawer.showSelectionMode === "function") {
      giftDrawer.showSelectionMode({
        parentItemKey: parentKey,
        parentUuid: parentUuid, // ← ADD THIS
        existingGiftLineId: existingGiftKey,
        existingGiftVariantId,
        parentQuantity:
          typeof parentQuantity === "number"
            ? parentQuantity
            : parseInt(parentQuantity, 10),
        // Editing gifts on a box already in the cart: keep the user in the cart
        // after Update instead of sending them on to the add-ons step.
        skipAddonsRedirect: true,
      });
    }
  }

  /**
   * Handles subscription frequency change.
   *
   * IMPORTANT: When changing a selling plan, the line item's `key` changes.
   * We must use `line` (1-based index) to identify the item, not `id` (key).
   *
   * Strategy (leverages syncGiftQuantities for orphan cleanup):
   * - Update selling plan (1 call)
   * - syncGiftQuantities removes orphaned gift via batch update (efficient)
   * - If changing frequency (not removing): re-add gift with new parent key
   *
   * @param {Event} event - The change event (first param when no query params in on:change)
   */
  async handleSubscriptionChange(event) {
    const select = /** @type {HTMLSelectElement} */ (event.target);
    const newSellingPlanId = select.value;
    const selectedOption = select.selectedOptions?.[0];
    const frequency = selectedOption?.dataset?.frequency;

    // Read data from select element's data attributes (populated by Liquid)
    const lineKey = select.dataset.lineKey;
    const lineIndex = parseInt(select.dataset.lineIndex || "0", 10);
    const giftVariantId = select.dataset.giftVariantId;
    const parentQuantity = parseInt(select.dataset.quantity || "1", 10);
    const parentVariantId = select.dataset.variantId;
    const productId = select.dataset.productId;
    const productName = select.dataset.productName;

    if (!lineIndex) {
      console.error("Cannot change subscription: missing line index");
      return;
    }

    if (this.#loading) return;

    // Queue this operation
    return cartOperationQueue.enqueue(
      () =>
        this.#executeSubscriptionChange(
          select,
          newSellingPlanId,
          lineKey,
          lineIndex,
          giftVariantId,
          parentQuantity,
          parentVariantId,
          { frequency, productId, productName },
        ),
      { id: `subscription-change-${lineIndex}`, priority: 1 },
    );
  }

  /**
   * Executes the subscription change operation
   * @param {HTMLSelectElement} select
   * @param {string} newSellingPlanId
   * @param {string | undefined} lineKey
   * @param {number} lineIndex
   * @param {string | undefined} giftVariantId
   * @param {number} parentQuantity
   * @param {string | undefined} parentVariantId
   * @param {{frequency?: string, productId?: string, productName?: string}} analytics
   */
  async #executeSubscriptionChange(
    select,
    newSellingPlanId,
    lineKey,
    lineIndex,
    giftVariantId,
    parentQuantity,
    parentVariantId,
    { frequency, productId, productName } = {},
  ) {
    this.#loading = true;
    this.#disableCart();

    const isRemovingSubscription = newSellingPlanId === "one-time";

    // Capture ALL gift lines under the old parent BEFORE the plan change. The
    // single `giftVariantId` from the select dataset only names the first gift;
    // a pick-N parent has more than one, and re-adding just one would collapse
    // two gifts into one (ACQ-247 T15). We re-link the full set after the change.
    let oldGiftLines = [];
    let oldParentUuid;
    try {
      const preCart = await getCart({ force: true });
      const oldParent = preCart.items?.find(
        (/** @type {CartItem} */ item) => item.key === lineKey,
      );
      oldParentUuid = oldParent?.properties?._line_item_id;
      if (oldParentUuid) {
        oldGiftLines = (preCart.items || []).filter(
          (/** @type {CartItem} */ item) =>
            item.properties?._is_pyp_gift === "true" &&
            item.properties?._parent_item_id === oldParentUuid,
        );
      }
    } catch (error) {
      console.error(
        "[CartDrawer] Failed to read gifts before plan change:",
        error,
      );
    }
    // Fall back to the single dataset variant if the pre-read found nothing.
    if (oldGiftLines.length === 0 && giftVariantId) {
      oldGiftLines = [{ variant_id: giftVariantId }];
    }
    const hadGift = oldGiftLines.length > 0;
    const isPickNGift =
      oldGiftLines.length >= 2 ||
      oldGiftLines.some(
        (/** @type {any} */ g) => g.properties?._gwp_slot !== undefined,
      );

    try {
      // Step 1: Update the selling plan (this changes the line item's key)
      const response = await fetch(
        Theme.routes.cart_change_url,
        fetchConfig("json", {
          body: JSON.stringify({
            line: lineIndex,
            quantity: parentQuantity,
            selling_plan: isRemovingSubscription ? "" : newSellingPlanId,
            sections: this.sectionId,
            sections_url: window.location.pathname,
          }),
        }),
      );
      const result = await response.json();

      if (result.errors) {
        console.error("Subscription update error:", result.errors);
        select.value = select.dataset.currentPlan || "one-time";
        return;
      }

      this.#primeCart(result, cartOperationQueue.currentSequence);

      // Step 2: Let syncGiftQuantities handle orphaned gift removal
      const syncResult = await syncGiftQuantitiesSingleFlight(result, {
        dispatchEvent: false,
        sections: [this.sectionId],
      });

      let finalCart = syncResult.cart || result;
      let mergedSections = result.sections ? { ...result.sections } : undefined;
      const syncedSectionHtml = syncResult.sections?.[this.sectionId];
      if (syncedSectionHtml) {
        mergedSections = { ...(mergedSections || {}), ...syncResult.sections };
      }
      let sectionHtml = syncedSectionHtml || result.sections?.[this.sectionId];
      const finalItemCount = syncResult.itemCount ?? finalCart.item_count;

      // Step 3: If changing frequency (not removing sub) AND had a gift, re-add it with new parent key
      if (!isRemovingSubscription && hadGift && parentVariantId) {
        const parentSource = finalCart.items || result.items;
        const newParentItem = parentSource?.find(
          (/** @type {CartItem} */ item) =>
            String(item.variant_id) === String(parentVariantId) &&
            item.selling_plan_allocation &&
            !item.properties?._is_pyp_gift,
        );

        if (newParentItem && newParentItem.key !== lineKey) {
          // Defensive: remove any gift lines still linked to the old/new parent
          // UUID before re-adding. Normally syncGiftQuantities already removed
          // the orphaned old gifts above, but if the box's _line_item_id survived
          // the plan change they'd still be linked and re-adding would duplicate
          // (ACQ-247 T15). Idempotent — a no-op when nothing is left.
          const linkedUuids = new Set(
            [oldParentUuid, newParentItem.properties?._line_item_id].filter(
              Boolean,
            ),
          );
          const leftoverGifts = (finalCart.items || []).filter(
            (/** @type {CartItem} */ item) =>
              item.properties?._is_pyp_gift === "true" &&
              linkedUuids.has(item.properties?._parent_item_id),
          );
          for (const gift of leftoverGifts) {
            const rmResp = await fetch(
              Theme.routes.cart_change_url,
              fetchConfig("json", {
                body: JSON.stringify({ id: gift.key, quantity: 0 }),
              }),
            );
            // Bail before re-adding if a removal failed — proceeding would be the
            // exact duplicate this cleanup prevents.
            if (!rmResp.ok) throw new Error("Leftover gift removal failed");
          }

          let parentUuid = newParentItem.properties?._line_item_id;
          if (!parentUuid) {
            parentUuid = crypto.randomUUID();
            // First update parent with UUID. Pick-N parents do NOT get
            // _has_double_gwp (it overrides double_gwp, D9); legacy single keeps it.
            /** @type {Record<string, string>} */
            const parentProps = {
              ...(newParentItem.properties || {}),
              _line_item_id: parentUuid,
              _parent_product: "yes",
            };
            if (!isPickNGift) {
              parentProps._has_double_gwp = Theme?.features?.double_gwp_enabled
                ? "true"
                : "false";
            } else {
              // Pick-N parents never carry _has_double_gwp (D9); strip any stale
              // value carried over from the spread of newParentItem.properties.
              delete parentProps._has_double_gwp;
            }
            const parentUpdateResponse = await fetch(
              Theme.routes.cart_change_url,
              fetchConfig("json", {
                body: JSON.stringify({
                  id: newParentItem.key,
                  quantity: newParentItem.quantity,
                  properties: parentProps,
                }),
              }),
            );
            if (!parentUpdateResponse.ok) {
              const text = await parentUpdateResponse.text().catch(() => "");
              // Bail before adding gifts: linking them to a parentUuid that was
              // never persisted would leave the reconciler with orphaned lines.
              throw new Error(
                `Parent property update failed (${parentUpdateResponse.status}): ${text}`,
              );
            }
          }

          const bundleId =
            newParentItem.properties?._rb_id ||
            newParentItem.properties?._bundle_id ||
            null;
          /** @type {any[]} */
          let giftLines;
          if (isPickNGift) {
            // Pick-N: re-add ALL gifts as slotted quantity-1 lines (T15).
            giftLines = buildGiftLineItems(
              groupGiftLinesToSelections(oldGiftLines),
              { parentUuid, bundleId },
            );
          } else {
            // Legacy single gift: preserve the original re-add verbatim — one
            // line at the parent quantity, minimal props (the reconciler applies
            // the double_gwp multiplier).
            giftLines = [
              {
                id: String(oldGiftLines[0]?.variant_id ?? giftVariantId),
                quantity: newParentItem.quantity,
                properties: {
                  _is_pyp_gift: "true",
                  _parent_item_id: parentUuid,
                },
              },
            ];
          }
          /** @type {Record<string, any>} */
          const addBody = { items: giftLines };
          if (this.sectionId) addBody.sections = this.sectionId;

          const addResponse = await fetch(Theme.routes.cart_add_url, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Requested-With": "XMLHttpRequest",
              Accept: "application/json",
            },
            body: JSON.stringify(addBody),
          });
          if (!addResponse.ok) {
            const text = await addResponse.text().catch(() => "");
            console.error("Gift re-add failed:", addResponse.status, text);
            // Don't update currentPlan; fall back to normal render below.
            throw new Error("Gift re-add failed");
          }

          const addResult = await addResponse.json();
          if (addResult?.status || addResult?.errors) {
            console.error("Gift re-add error response:", addResult);
            throw new Error("Gift re-add error");
          }

          // Update current plan tracking only after successful gift re-add
          select.dataset.currentPlan = isRemovingSubscription
            ? ""
            : newSellingPlanId;

          // /cart/add.js returns only the added line(s), not item_count or the
          // full items array. #finalizeCartUpdate primes the cache and reads
          // cart.item_count, so hand it a real cart snapshot; reuse the sections
          // the add already rendered for the morph.
          const freshCart = await getCart({ force: true });
          await this.#finalizeCartUpdate(
            freshCart,
            addResult.sections?.[this.sectionId],
            { ...(mergedSections || {}), ...addResult.sections },
          );
          return;
        }
      }

      // Update current plan tracking
      select.dataset.currentPlan = isRemovingSubscription
        ? ""
        : newSellingPlanId;

      // Track analytics
      const cartContext =
        window.grAnalytics?.buildCartContextFromCart?.(finalCart) || undefined;
      if (isRemovingSubscription) {
        window.grAnalytics?.trackOtpBoxAdded?.(
          {
            product_id: productId,
            product_name: productName,
          },
          cartContext,
        );
      } else {
        window.grAnalytics?.trackSubscriptionBoxAdded?.(
          {
            frequency: frequency || undefined,
            product_id: productId,
            product_name: productName,
          },
          cartContext,
        );
      }

      await this.#finalizeCartUpdate(
        finalCart,
        sectionHtml,
        mergedSections,
        finalItemCount,
      );
    } catch (error) {
      console.error("Error changing subscription:", error);
      // Force re-enable all quantity selectors on error
      this.#forceRestoreAllQuantitySelectors();
    } finally {
      this.#loading = false;
      this.#enableCart();
    }
  }
}

if (!customElements.get("gr-cart-drawer")) {
  customElements.define("gr-cart-drawer", GRCartDrawerComponent);
}

// The drawer module loads on every page, so this is the one registration
// point for the ACQ-299 free-gift reconciler (idempotent).
initFreeGiftReconciler();
